import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CheckPageAccess } from 'src/app/components/authorize/check-page-access';
import { PendingRoleOnboarding } from 'src/app/components/role-onboarding/role-onboarding';
import { resolveVideoEmbed, VideoEmbedType } from 'src/app/components/role-onboarding/video-embed';
import { AuthService } from 'src/app/services/auth.service';
import { ParticipantThemeService } from 'src/app/services/participant-theme.service';
import { RoleOnboardingService } from 'src/app/services/role-onboarding.service';
import { ToastService } from 'src/app/services/toast-service.service';

@Component({
    selector: 'app-complete-onboarding',
    templateUrl: './complete-onboarding.component.html',
    styleUrls: ['./complete-onboarding.component.scss'],
    standalone: false
})
export class CompleteOnboardingComponent {
  pageName = CheckPageAccess.ONBOARDINGTRAINING;
  pending: PendingRoleOnboarding[] = [];
  current: PendingRoleOnboarding | null = null;
  acknowledged = false;
  isSubmitting = false;
  embedType: VideoEmbedType | null = null;
  safeEmbedUrl: SafeResourceUrl | null = null;
  videoSrc = '';
  bannerClass = 'bb-coached-color';
  llttfPage = false;
  private skippedIds = new Set<number>();
  mandatoryRemaining = 0;
  optionalRemaining = 0;

  constructor(
    private roleOnboardingService: RoleOnboardingService,
    private authService: AuthService,
    private themeService: ParticipantThemeService,
    private router: Router,
    private toast: ToastService,
    private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    this.themeService.setParticipantTheme();
    this.llttfPage = this.themeService.llttfPage;
    this.bannerClass = this.authService.getUserLoginData().isParticipant
      ? (this.themeService.participantClassByRole || 'bb-coached-color')
      : 'bb-coached-color';
    this.loadPending();
  }

  loadPending(): void {
    this.roleOnboardingService.getPending().subscribe({
      next: (data) => {
        this.pending = (data ?? []).filter(c => c.isMandatory || !this.skippedIds.has(c.id));
        this.mandatoryRemaining = this.pending.filter(c => c.isMandatory).length;
        this.optionalRemaining = this.pending.length - this.mandatoryRemaining;
        if (this.pending.length === 0) {
          this.continueToPortal();
          return;
        }
        this.current = this.pending[0];
        this.acknowledged = false;
        this.setEmbed(this.current.videoUrl);
      }
    });
  }

  submit(): void {
    if (!this.current || !this.acknowledged || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.roleOnboardingService.acknowledge(this.current.id).subscribe({
      next: (ok) => {
        this.isSubmitting = false;
        if (ok) {
          this.loadPending();
        } else {
          this.toast.showErrorMessage('Unable to record acknowledgement');
        }
      },
      error: () => {
        this.isSubmitting = false;
        this.toast.showErrorMessage('Unable to record acknowledgement');
      }
    });
  }

  skip(): void {
    if (!this.current || this.current.isMandatory) {
      return;
    }

    this.skippedIds.add(this.current.id);
    this.loadPending();
  }

  private continueToPortal(): void {
    if (this.authService.getUserLoginData().isParticipant) {
      this.router.navigateByUrl('/dashboard-participant');
    } else {
      this.router.navigateByUrl('/dashboard-bb');
    }
  }

  private setEmbed(url: string): void {
    const embed = resolveVideoEmbed(url, this.sanitizer);
    this.embedType = embed.embedType;
    this.safeEmbedUrl = embed.safeEmbedUrl;
    this.videoSrc = embed.videoSrc;
  }
}
